// Short human-friendly location for outreach messages (2026-10-05).
//
// PRIVACY RULE: outreach/availability texts must show only City, State ZIP —
// some linguists are home-based and the STREET must never be shared before
// the assignment is theirs. This intentionally differs from
// `InvoiceHelpers.parseAddress` (which keeps street lines for invoices).
//
// Handles the address shapes seen in real data:
//   "8 West King Street, Lancaster, PA 17603"          → "Lancaster, PA 17603"
//   "65 S. Franklin St. Apt. 1, Lancaster PA 17602"    → "Lancaster, PA 17602"
//   "448 W. Vine St, Apt 2 Lancaster PA 17603"         → "Lancaster, PA 17603" (unit stripped)
//   "CNS ... 151 Koser Rd Lititz, PA 17543"            → "Lititz, PA 17543"   (city merged into street part)
//   "Manor Middle School\n2950 Charlestown Road\nLancaster, PA 17603" → "Lancaster, PA 17603" (newlines)
//   "UPMC, 205 S Front St, #8th Floor, Harrisburg, PA, 17104, US" → "Harrisburg, PA 17104"
//   "1040 E. Orange St., Lancaster"                    → "Lancaster"          (no ZIP at all)
//   "Lebanon, PA" / "Newak, USA"                       → returned joined
//   A url / non-address blob → "" (the line is omitted)

const STREET_SUFFIX_WORDS = /^(st\.?|street|rd\.?|road|ave\.?|avenue|blvd\.?|boulevard|dr\.?|drive|ln\.?|lane|ct\.?|court|way|pike|hwy\.?|highway|pl\.?|place|sq\.?|square|ter\.?|terrace|cir\.?|circle|fl\.?|floor|n|s|e|w|north|south|east|west)$/i;

const stripUnitPrefix = (value) =>
  String(value || "")
    .replace(/^(?:apt\.?|apartment|unit|ste\.?|suite|#)\s*[A-Za-z0-9-]+\s+/i, "")
    .trim();

export const getOutreachLocation = (address) => {
  const raw = String(address || "")
    .replace(/\r?\n+/g, ", ")
    .split(" | ")[0]
    .trim();
  if (!raw) return "";

  const parts = raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return "";

  // Find the segment that ends with a ZIP (search from the end).
  let zipIndex = -1;
  let zipMatch = null;
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const match = parts[i].match(/(\d{5}(?:-\d{4})?)\s*$/);
    if (match) {
      zipIndex = i;
      zipMatch = match;
      break;
    }
  }

  if (zipIndex === -1) {
    // No ZIP anywhere — the last segment is the best city guess. Join a
    // trailing short token like a state/region ("Lebanon, PA", "Newak, USA").
    const last = parts[parts.length - 1];
    const prev = parts[parts.length - 2];
    if (last.length > 40) return ""; // a URL / blob, not a location
    if (prev && /^[A-Za-z.]{2,4}$/.test(last)) return `${prev}, ${last}`;
    return last;
  }

  const zip = zipMatch[1];
  const beforeZip = parts[zipIndex].slice(0, zipMatch.index).trim();

  let state = "";
  let city = "";

  // The last segment often holds "City ST 12345" (city/state merged).
  const stateInSame = beforeZip.match(/\b([A-Za-z]{2})$/);
  if (stateInSame && beforeZip.length > 2) {
    state = stateInSame[1].toUpperCase();
    city = beforeZip.slice(0, beforeZip.length - stateInSame[1].length).trim();
  } else if (/^[A-Za-z]{2}$/.test(beforeZip)) {
    state = beforeZip.toUpperCase();
  }

  // Or the state lives in its own segment ("…, Harrisburg, PA, 17104, US").
  if (!state) {
    const prev = parts[zipIndex - 1] || "";
    const prevState = prev.match(/^([A-Za-z]{2})$/);
    if (prevState) {
      state = prevState[1].toUpperCase();
      city = parts[zipIndex - 2] || "";
    }
  }

  if (!city) {
    const prev = parts[zipIndex - 1] || "";
    city = /^[A-Za-z]{2}$/.test(prev) ? parts[zipIndex - 2] || "" : prev;
  }

  city = stripUnitPrefix(city);

  // If the guessed "city" still looks like a street (contains a house
  // number), keep only its last word when that isn't a street suffix
  // ("…Rd Lititz" → "Lititz"); otherwise drop it (privacy over guessing).
  if (/\d/.test(city)) {
    const words = city.split(/\s+/);
    const lastWord = words[words.length - 1] || "";
    city = STREET_SUFFIX_WORDS.test(lastWord) ? "" : lastWord;
  }

  const tail = [state, zip].filter(Boolean).join(" ");
  return [city, tail].filter(Boolean).join(", ");
};
